import {
  APICallState,
  MFAActions,
  MFAChannel,
  PreferenceSelectorState,
  Selectors,
  VerifyProfileType,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library';
import {useCallback, useEffect, useState} from 'react';
import {ROUTES} from 'routes';

import CodeVerification from 'components/CodeVerification';
import {useNavigateWithParams} from 'hooks/useNavigateWithParams';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

const ConnectedCodeVerification: React.FunctionComponent<{
  channel: MFAChannel;
  contactInfoValue: string;
}> = ({channel, contactInfoValue}) => {
  const navigate = useNavigateWithParams();
  const widgetDispatch = useWidgetDispatch();
  const verifyProfileState = useWidgetState(
    Selectors.verifyProfileStateSelector,
  );
  const editProfileState = useWidgetState(Selectors.editProfileStateSelector);

  const isVip = useWidgetState(Selectors.isVIPSelector) as boolean;
  const isCodeInvalid = verifyProfileState === APICallState.INVALID_CODE;
  const isLoading = [verifyProfileState, editProfileState].includes(
    APICallState.LOADING,
  );
  const isError = verifyProfileState === APICallState.ERROR;
  const preferenceSelectorState = useWidgetState(
    Selectors.preferenceSelectorStateSelector,
  ) as PreferenceSelectorState;
  const {trackAdobeEvent} = useTrackEvent();
  const isEmail = channel === MFAChannel.EMAIL;
  const [numberOfCodeAttempts, setNumberOfCodeAttempts] = useState<number>(0);
  const maxCodeAttempts = 3;

  useEffect(() => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: `mfa ${channel} verification sent`,
      },
    });
  }, []);

  useEffect(() => {
    if (isCodeInvalid) {
      trackAdobeEvent({
        event: AdobeEvent.click,
        click: {
          name: `mfa ${channel} verification invalid`,
        },
      });
    }
  }, [isCodeInvalid]);

  const verifyCode = useCallback(
    (code: string) => {
      const {AddEmail, EditEmail} = PreferenceSelectorState;
      const type = [AddEmail, EditEmail].includes(preferenceSelectorState)
        ? VerifyProfileType.EMAIL
        : VerifyProfileType.SMS;
      widgetDispatch(MFAActions.verifySecurityProfile({passcode: code, type}));
      setNumberOfCodeAttempts(
        (numberOfCodeAttempts: number) => numberOfCodeAttempts + 1,
      );
    },
    [preferenceSelectorState, widgetDispatch],
  );

  const onResendCode = useCallback(() => {
    const action = isEmail
      ? MFAActions.editSecurityEmail({
          securityEmail: contactInfoValue,
          isUpdate: true,
        })
      : MFAActions.editSecurityPhone({
          securityPhone: contactInfoValue,
          isUpdate: true,
        });

    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: `mfa ${channel} verification resent`,
      },
    });

    widgetDispatch(MFAActions.verifySecurityProfileReset());
    widgetDispatch(action);
    setNumberOfCodeAttempts(0);
  }, [channel, contactInfoValue, isEmail, trackAdobeEvent, widgetDispatch]);

  if (verifyProfileState === APICallState.SUCCESS) {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: `mfa ${channel} verification success`,
      },
    });

    switch (preferenceSelectorState) {
      case PreferenceSelectorState.AddEmail:
      case PreferenceSelectorState.AddPhone:
      case PreferenceSelectorState.EditEmail:
      case PreferenceSelectorState.EditPhone:
        if (isEmail) {
          widgetDispatch(MFAActions.setSecurityEmail(contactInfoValue));
        } else {
          widgetDispatch(MFAActions.setMobilePhone(contactInfoValue));
        }
        widgetDispatch(
          MFAActions.changeChannelPreference({
            channelPreference: channel,
          }),
        );
        navigate(ROUTES.confirmationSuccess, {state: channel});
        break;
      default:
        navigate(ROUTES.verifyIdentity);
        break;
    }
    widgetDispatch(MFAActions.verifySecurityProfileReset());
    return null;
  }

  return (
    <CodeVerification
      isError={isError}
      isCodeInvalid={isCodeInvalid}
      mobilePhone={!isEmail ? contactInfoValue : undefined}
      onCodeEnter={verifyCode}
      onResendCode={onResendCode}
      isLoading={isLoading}
      clearState={() => widgetDispatch(MFAActions.verifySecurityProfileReset())}
      channel={channel}
      email={isEmail ? contactInfoValue : undefined}
      isVip={isVip}
      isOverMaxCodeAttempts={numberOfCodeAttempts >= maxCodeAttempts}
    />
  );
};

export default ConnectedCodeVerification;
